(() => {
  'use strict';
  const bound = new WeakSet();
  const desktop = window.matchMedia('(min-width:1100px) and (hover:hover) and (pointer:fine)');
  const mobile = window.matchMedia('(max-width:749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion:reduce)');
  const lockState = () => document.body.classList.toggle('px-modal-open', Boolean(document.querySelector('dialog[data-store-dialog][open]')));
  const claim = element => {
    if (bound.has(element)) return false;
    bound.add(element);
    return true;
  };
  function initialize(root = document) {
    root.querySelectorAll('[data-store-card]').forEach(card => {
      if (!claim(card)) return;
      const button = card.querySelector('[data-store-preview]');
      const dialog = card.querySelector('[data-store-dialog]');
      if (!button || !dialog || typeof dialog.showModal !== 'function') return;
      const select = dialog.querySelector('[data-store-variant]');
      const synchronize = () => {
        const option = select?.selectedOptions[0];
        if (!option) return;
        const price = dialog.querySelector('[data-store-price]');
        const details = dialog.querySelector('[data-store-details]');
        const image = dialog.querySelector('[data-preview-image] img');
        const placeholder = dialog.querySelector('[data-preview-placeholder]');
        if (price) price.textContent = option.dataset.price || '';
        if (details && option.dataset.url) details.href = option.dataset.url;
        const hasImage = Boolean(option.dataset.image);
        if (image) {
          image.hidden = !hasImage;
          if (hasImage) {
            image.removeAttribute('srcset');
            image.src = option.dataset.image;
            image.alt = option.textContent.trim();
          }
        }
        if (placeholder) placeholder.hidden = hasImage;
      };
      button.hidden = false;
      button.addEventListener('click', () => {
        synchronize();
        if (!dialog.open) dialog.showModal();
        lockState();
      });
      select?.addEventListener('change', synchronize);
      dialog.querySelector('[data-store-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        lockState();
        if (button.isConnected) button.focus({ preventScroll: true });
      });
    });
    root.querySelectorAll('[data-pixel-gallery]').forEach(gallery => {
      if (!claim(gallery)) return;
      const panels = [...gallery.querySelectorAll('[data-gallery-panel]')];
      const buttons = [...gallery.querySelectorAll('[data-gallery-to]')];
      const track = gallery.querySelector('.px-gallery__panels');
      if (panels.length < 2 || !buttons.length || !track) return;
      let selected = 0;
      let frame = 0;
      const paint = () => {
        panels.forEach((panel, index) => {
          panel.hidden = !mobile.matches && index !== selected;
          if (index !== selected) panel.querySelectorAll('video').forEach(video => video.pause());
        });
        buttons.forEach((button, index) => {
          button.hidden = false;
          button.setAttribute('aria-pressed', String(index === selected));
        });
      };
      const choose = index => {
        selected = Math.max(0, Math.min(panels.length - 1, index));
        paint();
        if (mobile.matches) track.scrollTo({ left: selected * track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
      };
      const synchronize = () => {
        gallery.dataset.pGalleryMode = mobile.matches ? 'scroll' : 'single';
        track.tabIndex = mobile.matches ? 0 : -1;
        paint();
        track.scrollTo({ left: mobile.matches ? selected * track.clientWidth : 0, behavior: 'auto' });
      };
      buttons.forEach(button => button.addEventListener('click', () => choose(Number(button.dataset.galleryTo))));
      track.addEventListener('scroll', () => {
        if (!mobile.matches || frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          selected = Math.max(0, Math.min(panels.length - 1, Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
          paint();
        });
      }, { passive: true });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        choose(selected + (event.key === 'ArrowRight' ? 1 : -1));
      });
      mobile.addEventListener('change', synchronize);
      synchronize();
    });
    root.querySelectorAll('[data-ind-choices]').forEach(choices => {
      if (!claim(choices)) return;
      const select = choices.closest('form')?.querySelector('[data-variant-select]');
      if (!select) return;
      const buttons = [...choices.querySelectorAll('[data-ind-variant]')];
      const synchronize = () => buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.indVariant === select.value)));
      buttons.forEach(button => button.addEventListener('click', () => {
        if (![...select.options].some(option => option.value === button.dataset.indVariant)) return;
        select.value = button.dataset.indVariant;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      select.addEventListener('change', synchronize);
      choices.hidden = false;
      select.hidden = true;
      synchronize();
    });
    root.querySelectorAll('[data-qty-step]').forEach(button => {
      if (!claim(button)) return;
      button.addEventListener('click', () => {
        const input = button.closest('.px-quantity')?.querySelector('input[type=number]');
        if (!input || input.disabled) return;
        const step = Number(input.step) || 1;
        const minimum = input.min === '' ? 0 : Number(input.min);
        const maximum = input.max === '' ? Infinity : Number(input.max);
        const current = input.value === '' || !Number.isFinite(Number(input.value)) ? minimum : Number(input.value);
        input.value = String(Math.max(minimum, Math.min(maximum, current + step * Number(button.dataset.qtyStep))));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    root.querySelectorAll('[data-sort-submit], [data-market-select]').forEach(select => {
      if (!claim(select)) return;
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
    root.querySelectorAll('.px-catalog-sidebar').forEach(sidebar => {
      if (!claim(sidebar)) return;
      const heading = sidebar.querySelector(':scope > h2');
      if (!heading) return;
      const details = document.createElement('details');
      const summary = document.createElement('summary');
      const content = document.createElement('div');
      details.className = 'p-catalog-filters';
      summary.textContent = heading.textContent;
      content.className = 'p-catalog-filters__body';
      let next = heading.nextSibling;
      while (next) {
        const following = next.nextSibling;
        content.append(next);
        next = following;
      }
      heading.remove();
      details.append(summary, content);
      sidebar.append(details);
      const synchronize = () => { details.open = !mobile.matches || Boolean(details.querySelector('input:checked')); };
      mobile.addEventListener('change', synchronize);
      synchronize();
    });
    root.querySelectorAll('[data-site-header] [data-site-submenu]').forEach(menu => {
      if (!claim(menu)) return;
      let timer;
      const summary = menu.querySelector(':scope > summary');
      const open = () => { clearTimeout(timer); if (desktop.matches) menu.open = true; };
      const close = () => {
        clearTimeout(timer);
        if (desktop.matches) timer = setTimeout(() => { if (!menu.contains(document.activeElement)) menu.open = false; }, 150);
      };
      menu.addEventListener('pointerenter', open);
      menu.addEventListener('pointerleave', close);
      menu.addEventListener('focusin', open);
      menu.addEventListener('focusout', close);
      summary?.addEventListener('click', event => { if (desktop.matches) { event.preventDefault(); menu.open = true; } });
      menu.addEventListener('keydown', event => {
        if (event.key === 'Escape' && menu.open) {
          summary?.focus();
          menu.open = false;
          event.stopPropagation();
        }
      });
    });
    root.querySelectorAll('.dy-product-form').forEach(form => {
      if (!claim(form)) return;
      const product = form.closest('.px-product');
      const source = form.querySelector('[data-add-to-cart]');
      const bar = document.querySelector('[data-p-buybar]');
      if (!product || !source || !bar || bar.querySelector('button')?.form !== form || !('IntersectionObserver' in window)) return;
      const target = bar.querySelector('[data-p-buy-submit]');
      const price = bar.querySelector('[data-p-buy-price]');
      const title = bar.querySelector('[data-p-buy-title]');
      let above = false;
      let footerVisible = false;
      let frame = 0;
      const paint = () => {
        const visible = mobile.matches && above && !footerVisible;
        bar.hidden = !visible;
        document.body.classList.toggle('p-buybar-active', visible);
      };
      const synchronize = () => {
        target.disabled = source.disabled;
        target.textContent = source.disabled ? source.dataset.soldOutLabel : source.dataset.availableLabel;
        price.textContent = product.querySelector('[data-product-price]')?.textContent || '';
        title.textContent = product.querySelector('h1')?.textContent || '';
      };
      const assess = () => {
        frame = 0;
        if (!mobile.matches || document.visibilityState === 'hidden') return;
        above = source.getBoundingClientRect().bottom < 80;
        paint();
      };
      const onScroll = () => { if (mobile.matches && !frame) frame = requestAnimationFrame(assess); };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === source) above = entry.boundingClientRect.bottom < 80;
          else footerVisible = entry.isIntersecting;
        });
        paint();
      }, { rootMargin: '-80px 0px 0px 0px', threshold: 0 });
      observer.observe(source);
      const footer = document.querySelector('footer');
      if (footer) observer.observe(footer);
      mobile.addEventListener('change', () => { assess(); paint(); });
      window.addEventListener('scroll', onScroll, { passive: true });
      form.addEventListener('change', () => queueMicrotask(synchronize));
      target.addEventListener('click', event => {
        synchronize();
        if (target.disabled) event.preventDefault();
      });
      const info = product.querySelector('.px-product__info');
      const mutations = new MutationObserver(synchronize);
      mutations.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      synchronize();
      assess();
      paint();
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(product)) return;
        observer.disconnect();
        mutations.disconnect();
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', onScroll);
        bar.hidden = true;
        document.body.classList.remove('p-buybar-active');
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  window.addEventListener('pageshow', lockState);
})();
